import { type MouseEvent, useEffect, useState } from 'react'

import { Search } from 'lucide-react'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { Song } from '@/core/api/entities/song/song'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePlaylistSample } from '@/core/hooks/usePlaylist'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import { Input } from '@/shared/components/atoms/Input'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { PlaylistRow } from '@/shared/components/molecules/PlaylistRow/PlaylistRow'
import Player from '@/shared/components/organisms/Player/Player'

interface PlaylistSelectorProps {
	value: number[]
	onChange: (value: number[]) => void
	onPercentagesChange?: (percentages: Record<string, number>) => void
	maxSelections?: number
	playlists: Playlist[]
	isLoading: boolean
	hasMorePages: boolean
	onLoadMore: () => void
	dialogOpen?: boolean
	onPlaylistsChange?: (playlists: Playlist[]) => void
	searchQuery: string
	onSearchQueryChange: (query: string) => void
	customPlaylistIds?: Set<number> | number[]
}

export const PlaylistSelector = ({
	value,
	onChange,
	onPercentagesChange,
	maxSelections = 2,
	playlists,
	isLoading,
	hasMorePages,
	onLoadMore,
	dialogOpen,
	onPlaylistsChange,
	searchQuery,
	onSearchQueryChange,
	customPlaylistIds,
}: PlaylistSelectorProps) => {
	const { t } = useTranslation()
	const [isLoadingMore, setIsLoadingMore] = useState(false)
	const [playingPlaylistId, setPlayingPlaylistId] = useState<number | null>(
		null
	)
	const [currentSong, setCurrentSong] = useState<Song | null>(null)
	// Track song index per playlist to rotate through cached sample songs
	const [songIndexByPlaylist, setSongIndexByPlaylist] = useState<
		Record<number, number>
	>({})

	// Fetch sample for the playing playlist (cached for 5 minutes)
	const { data: sampleSongs, isLoading: isLoadingSample } = usePlaylistSample({
		playlistId: playingPlaylistId,
		config: useAccountConfig({}),
		options: {
			enabled: playingPlaylistId !== null,
		},
	})

	// Set current song when sample data is loaded, using the tracked index
	useEffect(() => {
		if (sampleSongs && sampleSongs.length > 0 && playingPlaylistId !== null) {
			const currentIndex = songIndexByPlaylist[playingPlaylistId] ?? 0
			const songIndex = currentIndex % sampleSongs.length
			setCurrentSong(sampleSongs[songIndex])
		}
	}, [sampleSongs, playingPlaylistId, songIndexByPlaylist])

	// Reset internal state when dialog opens
	useEffect(() => {
		if (dialogOpen) {
			setIsLoadingMore(false)
			setPlayingPlaylistId(null)
			setCurrentSong(null)
		}
	}, [dialogOpen])

	// Get the currently playing playlist data
	const playingPlaylist = playlists.find((p) => p.id === playingPlaylistId)

	// Handle play/stop button click
	const handlePlayClick = (
		playlistId: number,
		e: MouseEvent<HTMLButtonElement>
	) => {
		e.stopPropagation()
		if (playingPlaylistId === playlistId) {
			// Stop playing
			setPlayingPlaylistId(null)
			setCurrentSong(null)
		} else {
			// Start playing this playlist
			// Increment the song index to pick the next song from the cached sample
			setSongIndexByPlaylist((prev) => ({
				...prev,
				[playlistId]: (prev[playlistId] ?? -1) + 1,
			}))
			setPlayingPlaylistId(playlistId)
			setCurrentSong(null) // Reset until sample loads
		}
	}

	// Handle player pause/stop
	const handlePlayerStop = () => {
		setPlayingPlaylistId(null)
		setCurrentSong(null)
	}

	// Sort playlists alphabetically (case-insensitive)
	const sortAlpha = (a: Playlist, b: Playlist) =>
		(a.title || '').localeCompare(b.title || '', undefined, {
			sensitivity: 'base',
		})

	const customIds =
		customPlaylistIds instanceof Set
			? customPlaylistIds
			: new Set<number>(customPlaylistIds || [])
	const hasCustomIds = customIds.size > 0

	// Always show selected playlists first, regardless of privacy.
	// After that, keep the existing private/public sections (unselected only).
	const selectedSet = new Set<number>(value || [])

	const selectedPlaylists = (playlists || [])
		.filter((p) => selectedSet.has(p.id))
		.sort(sortAlpha)

	// Split into custom (assigned) and public playlists (UNSELECTED only)
	const privatePlaylists = (playlists || [])
		.filter((p) => (hasCustomIds ? customIds.has(p.id) : p.public === false))
		.filter((p) => !selectedSet.has(p.id))
		.sort(sortAlpha)
	const publicPlaylists = (playlists || [])
		.filter((p) => (hasCustomIds ? !customIds.has(p.id) : p.public === true))
		.filter((p) => !selectedSet.has(p.id))
		.sort(sortAlpha)

	// Manual load more function
	const handleLoadMore = () => {
		if (hasMorePages && !isLoading && !isLoadingMore) {
			setIsLoadingMore(true)
			onLoadMore()
			setTimeout(() => setIsLoadingMore(false), 500)
		}
	}

	const handleCheckboxChange = (
		playlistId: number,
		checked: boolean | 'indeterminate'
	) => {
		const currentValue = value || []
		// Prevent selecting more than maxSelections
		if (checked && currentValue.length >= maxSelections) {
			return
		}
		const newValue = checked
			? [...currentValue, playlistId]
			: currentValue.filter((id) => id !== playlistId)
		onChange(newValue)

		// Call onPlaylistsChange with full playlist objects
		if (onPlaylistsChange) {
			const selectedPlaylists = newValue
				.map((id) => {
					const playlist = playlists.find((p) => p.id === id)
					return playlist
				})
				.filter(Boolean) as Playlist[]
			onPlaylistsChange(selectedPlaylists)
		}

		// Handle percentages if callback provided
		if (onPercentagesChange) {
			if (newValue.length === 1) {
				onPercentagesChange({ [newValue[0]]: 100 })
			} else if (newValue.length === 2 && checked) {
				const percentages: Record<string, number> = {}
				newValue.forEach((id) => {
					percentages[id] = 50
				})
				onPercentagesChange(percentages)
			}
		}
	}

	const renderSectionHeader = (label: string) => (
		<div className="px-4 py-2 text-[11px] font-medium tracking-wide text-muted-foreground uppercase bg-background/40 border-b">
			{label}
		</div>
	)

	const renderPlaylistRow = (playlist: Playlist) => {
		const isSelected = value?.includes(playlist.id) || false

		return (
			<PlaylistRow
				key={playlist.id}
				playlist={playlist}
				isSelected={isSelected}
				onSelectionChange={(checked) =>
					handleCheckboxChange(playlist.id, checked)
				}
				coverSizeClassName="w-10 h-10"
				coverImageClassName="rounded object-cover"
				coverFallbackClassName="bg-muted rounded"
				isPlaying={playingPlaylistId === playlist.id}
				isLoadingPlayback={isLoadingSample && playingPlaylistId === playlist.id}
				isPlaybackReady={Boolean(currentSong)}
				onPlayClick={(e) => handlePlayClick(playlist.id, e)}
			/>
		)
	}

	return (
		<div className="space-y-3">
			<div className="relative">
				<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
				<Input
					placeholder={t(
						'entities.schedule.playlist_selector.search_placeholder'
					)}
					value={searchQuery}
					onChange={(e) => onSearchQueryChange(e.target.value)}
					className="pl-10"
				/>
			</div>

			<ScrollArea className="h-48 border rounded-md">
				{isLoading && playlists.length === 0 ? (
					<div className="space-y-0">
						{Array.from({ length: 5 }).map((_, index) => (
							<div
								key={index}
								className="flex items-center justify-between px-4 py-3 border-b last:border-b-0"
							>
								<div className="flex items-center gap-3 flex-1">
									<Skeleton className="w-10 h-10 rounded" />
									<div className="flex-1 space-y-2">
										<Skeleton className="h-4 w-3/4" />
										<Skeleton className="h-3 w-1/2" />
									</div>
								</div>
								<Skeleton className="w-5 h-5 rounded" />
							</div>
						))}
					</div>
				) : selectedPlaylists.length +
						privatePlaylists.length +
						publicPlaylists.length ===
				  0 ? (
					<div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm p-4 gap-2">
						<p>{t('entities.schedule.playlist_selector.no_playlists_found')}</p>
					</div>
				) : (
					<>
						<div className="space-y-0">
							{selectedPlaylists.length > 0 &&
								renderSectionHeader(
									t(
										'entities.schedule.playlist_selector.section_selected',
										'Selected'
									)
								)}
							{selectedPlaylists.map(renderPlaylistRow)}

							{privatePlaylists.length > 0 &&
								renderSectionHeader(
									t(
										'entities.schedule.playlist_selector.section_private',
										'Your playlists'
									)
								)}
							{privatePlaylists.map(renderPlaylistRow)}

							{publicPlaylists.length > 0 &&
								renderSectionHeader(
									t(
										'entities.schedule.playlist_selector.section_public',
										'Public playlists'
									)
								)}
							{publicPlaylists.map(renderPlaylistRow)}
						</div>
						{hasMorePages && (
							<div className="p-3 border-t">
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={handleLoadMore}
									disabled={isLoadingMore || isLoading}
									className="w-full text-xs"
								>
									{isLoadingMore || isLoading
										? t('entities.schedule.playlist_selector.loading_more')
										: t('entities.schedule.playlist_selector.load_more')}
								</Button>
							</div>
						)}
					</>
				)}
			</ScrollArea>

			{/* Player will be hidden by default, since we don't want to lose focus, only play a sample */}
			{currentSong && playingPlaylist && (
				<div className="mt-3 border rounded-md overflow-hidden hidden">
					<Player
						src={currentSong.path}
						cover={playingPlaylist.getSmallestThumbnail()}
						title={
							currentSong.metadata.title ??
							t('entities.schedule.playlist_selector.unknown_track')
						}
						radioPrevText={t('entities.schedule.playlist_selector.preview')}
						radioTitle={playingPlaylist.title}
						metadata={[
							currentSong.metadata.artist ||
								t('entities.schedule.playlist_selector.unknown_artist'),
							currentSong.metadata.album || '',
						].filter(Boolean)}
						showSkipControls={false}
						showJumpControls={false}
						autoPlay={true}
						onPause={handlePlayerStop}
						hideVolume={true}
						cssVars={{
							border: 'none',
							backgroundColor: 'transparent',
						}}
					/>
				</div>
			)}
		</div>
	)
}
